import type { SidebarButtonProps } from '@carboncopyco/editor-ui';
import {
  AiIcon,
  BalloonOutlineIcon,
  CodeOutlineIcon,
  FindReplaceOutlineIcon,
  ImportOutlineIcon,
  LaunchIcon,
  LibraryAddOutlineIcon,
  ObjectIcon,
  SpliceOutlineIcon,
  TrackchangesOutlineIcon,
  UnClickIcon,
  VariableIcon,
} from '@carboncopyco/icons';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  useEditor,
  useEditorTransactionModifications,
} from '../../context/EditorSdkProvider';

export type Tool =
  | 'findReplace'
  | 'variable'
  | 'customCharts'
  | 'customCode'
  | 'transactions'
  | 'navigation'
  | 'importManager'
  | 'spotEditsManager'
  | 'objectsManager'
  | 'guidanceManager'
  | 'aiManager'
  | 'editorPerformanceManager';

export interface ButtonConfig<T extends Tool>
  extends Pick<SidebarButtonProps, 'icon' | 'tooltipMessage' | 'aria-label'> {
  tool: T;
  hide: boolean;
  hasBadge?: boolean;
}

export const useButtonsConfig = (): {
  [t in Tool]: ButtonConfig<t>;
} => {
  const {
    editorChartsCustomCodeEditor,
    editorDataRules,
    editorDataRulesAdmin,
    editorSpotEdits,
    editorAllowFullCapabilitiesInOverlay,
    editorAiManager,
    editorPerformance,
  } = useFlags();
  const { transactionModifications } = useEditorTransactionModifications();
  const { recordingType } = useEditor();

  return {
    findReplace: {
      tooltipMessage: 'Find & Replace',
      'aria-label': 'Toggle Find & Replace manager',
      icon: <FindReplaceOutlineIcon />,
      tool: 'findReplace',
      hide: false,
    },
    variable: {
      tooltipMessage: 'Variables',
      'aria-label': 'Toggle Variables manager',
      icon: <VariableIcon />,
      tool: 'variable',
      hide: false,
    },
    customCharts: {
      tooltipMessage: 'Custom Charts',
      'aria-label': 'Toggle Custom Charts manager',
      icon: <LibraryAddOutlineIcon />,
      tool: 'customCharts',
      hide: !editorChartsCustomCodeEditor,
    },
    customCode: {
      tooltipMessage: 'Custom Code',
      'aria-label': 'Toggle Custom Code manager',
      icon: <CodeOutlineIcon />,
      tool: 'customCode',
      hide: false,
    },
    transactions: {
      tooltipMessage: 'Data Rules',
      'aria-label': 'Toggle Data Rules manager',
      icon: <SpliceOutlineIcon />,
      tool: 'transactions',
      hide:
        !editorDataRules ||
        (!editorDataRulesAdmin &&
          transactionModifications.every(({ isLocked }) => isLocked)),
    },
    navigation: {
      tooltipMessage: 'Block Events',
      'aria-label': 'Toggle Buttons manager',
      icon: <UnClickIcon />,
      tool: 'navigation',
      hide: false,
    },
    importManager: {
      tooltipMessage: 'Import Edits',
      'aria-label': 'Import',
      icon: <ImportOutlineIcon />,
      tool: 'importManager',
      hide: false,
    },
    spotEditsManager: {
      tooltipMessage: 'Spot Edits',
      'aria-label': 'Toggle Edits',
      icon: <TrackchangesOutlineIcon />,
      tool: 'spotEditsManager',
      hide:
        !editorSpotEdits ||
        (recordingType === 'overlay' && !editorAllowFullCapabilitiesInOverlay),
    },
    objectsManager: {
      tooltipMessage: 'Database Editing',
      'aria-label': 'Toggle objects manager',
      icon: <ObjectIcon />,
      tool: 'objectsManager',
      hide: recordingType === 'overlay',
      hasBadge: true,
    },
    guidanceManager: {
      tooltipMessage: 'Guided Sandbox',
      'aria-label': 'Guidance',
      icon: <BalloonOutlineIcon />,
      tool: 'guidanceManager',
      hide: false,
    },
    aiManager: {
      tooltipMessage: 'AI Wizard',
      'aria-label': 'AI Wizard',
      icon: <AiIcon />,
      tool: 'aiManager',
      hide: !editorAiManager,
    },
    editorPerformanceManager: {
      tooltipMessage: 'Editor performance',
      'aria-label': 'Editor performance',
      icon: <LaunchIcon />,
      tool: 'editorPerformanceManager',
      hide: !editorPerformance,
    },
  };
};
